import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { setJustLoggedIn } from '../../hooks/useWelcomeAnimation';
import useSounds from '../../hooks/useSounds';
import { getUserProfile, isInvestor, initializeUserSubscription } from '../../services/userApi';

interface FirebaseError extends Error {
  code?: string;
}

interface GoogleSignInButtonProps {
  onError?: (error: string) => void;
  onSuccess?: () => void;
  className?: string;
  disabled?: boolean;
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onError,
  onSuccess,
  className = '',
  disabled = false,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const { playSound } = useSounds();

  const handleGoogleSignIn = async (): Promise<void> => {
    setLoading(true);

    try {
      const userCredential = await signInWithGoogle();
      playSound('login');
      setJustLoggedIn();
      onSuccess?.();

      // Initialize subscription immediately after sign-in using the user credentials we just received
      // This triggers the Telegram notification for new users and ensures user appears in admin panel
      try {
        await initializeUserSubscription(
          userCredential.user.uid,
          userCredential.user.email || ''
        );
      } catch (initError) {
        console.error('Failed to initialize subscription:', initError);
        // Continue with navigation even if subscription init fails
      }

      // Check if user is an investor - redirect to investor page instead of app
      try {
        const profile = await getUserProfile(userCredential.user.uid);
        if (isInvestor(profile.type)) {
          navigate('/investor');
          return;
        }
      } catch {
        // If profile fetch fails, just proceed to app
      }

      navigate('/app');
    } catch (err) {
      const firebaseError = err as FirebaseError;
      console.error(err);

      // Don't show error for user-cancelled actions
      if (firebaseError.code === 'auth/popup-closed-by-user') {
        // User closed the popup, no error message needed
      } else if (firebaseError.code === 'auth/cancelled-popup-request') {
        // Multiple popups, ignore
      } else if (firebaseError.code === 'auth/popup-blocked') {
        onError?.('Popup was blocked. Please allow popups for this site.');
      } else if (firebaseError.message) {
        onError?.(firebaseError.message);
      } else {
        onError?.('Failed to sign in with Google');
      }
    }

    setLoading(false);
  };

  return (
    <button
      type="button"
      onClick={handleGoogleSignIn}
      disabled={disabled || loading}
      className={`w-full py-3 px-4 border border-gray-300 dark:border-slate-600 rounded-lg font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 ${className}`}
    >
      <GoogleIcon />
      {loading ? 'Signing in...' : 'Continue with Google'}
    </button>
  );
};

// Separate Google icon component for reusability
export const GoogleIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export default GoogleSignInButton;
